import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { EntityFormMode } from '@/common/entityFramework/enumerations/EntityFormMode';
import { useForm } from '@/common/form/FormContext';
import { useFormField } from '@/common/form/hooks/useFormField';
import { DateUtils } from '@/common/utils/DateUtils';
import {
  Box,
  Chip,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

// Utility to build select options
const range = (start, end) =>
  Array.from({ length: end - start + 1 }, (_, i) =>
    i.toString().padStart(2, '0'),
  );

const seconds = ['00'];
const minutes = ['*', '*/5', '*/10', '*/15', '*/30', ...range(0, 59)];
const hours = ['*', '*/2', '*/3', '*/4', '*/6', '*/12', ...range(0, 23)];
const days = ['*', '?', ...range(1, 31)];
const months = ['*', ...range(1, 12)];
const WEEKDAY_ORDER = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const weekdays = ['*', '?', ...WEEKDAY_ORDER];

function parseWeekdayValue(value) {
  if (/^\d+$/.test(value)) {
    return Number(value);
  }

  const idx = WEEKDAY_ORDER.indexOf(value.toUpperCase());
  return idx === -1 ? null : idx;
}

function formatWeekdayValue(value) {
  const parsed = parseWeekdayValue(value);
  if (parsed === null || parsed < 0 || parsed > 6) return value;
  return WEEKDAY_ORDER[parsed];
}

const partConfigs = [
  {
    label: 'Seconds',
    values: seconds,
    min: 0,
    max: 59,
    unit: 'second',
    supportsAdvanced: false,
  },
  {
    label: 'Minutes',
    values: minutes,
    min: 0,
    max: 59,
    unit: 'minute',
    supportsAdvanced: true,
  },
  {
    label: 'Hours',
    values: hours,
    min: 0,
    max: 23,
    unit: 'hour',
    supportsAdvanced: true,
  },
  {
    label: 'Day of Month',
    values: days,
    min: 1,
    max: 31,
    unit: 'day',
    supportsAdvanced: true,
    allowQuestionMark: true,
  },
  {
    label: 'Month',
    values: months,
    min: 1,
    max: 12,
    unit: 'month',
    supportsAdvanced: true,
  },
  {
    label: 'Day of Week',
    values: weekdays,
    min: 0,
    max: 6,
    unit: 'weekday',
    supportsAdvanced: true,
    allowQuestionMark: true,
    allowAlphabetic: true,
    parseValue: parseWeekdayValue,
  },
];

const listFormatter = new Intl.ListFormat('en', { type: 'conjunction' });
const listFormat = (values) => listFormatter.format(values);

const parseNumber = (value) => {
  if (!/^\d+$/.test(value)) return null;
  return Number(value);
};

const passthroughValueFormatter = (value) => value;
const weekdayValueFormatter = (value) => formatWeekdayValue(value);

const validateAtomicToken = (
  baseToken,
  min,
  max,
  allowQuestionMark = false,
  parseValue = parseNumber,
) => {
  if (baseToken === '*') return null;
  if (allowQuestionMark && baseToken === '?') return null;

  if (baseToken.includes('-')) {
    const [left, right] = baseToken.split('-');
    if (!left || !right) return 'Ranges must include both endpoints';
    const start = parseValue(left);
    const end = parseValue(right);
    if (start === null || end === null)
      return 'Range endpoints must be integers';
    if (start > end)
      return 'Range start must be less than or equal to range end';
    if (start < min || end > max)
      return `Range values must be between ${min} and ${max}`;
    return null;
  }

  const single = parseValue(baseToken);
  if (single === null)
    return 'Values must be integers, ranges, lists, or steps';
  if (single < min || single > max)
    return `Values must be between ${min} and ${max}`;
  return null;
};

const validateCronPartToken = (
  token,
  min,
  max,
  allowQuestionMark = false,
  parseValue = parseNumber,
) => {
  if (!token) return 'Empty tokens are not allowed';

  const stepParts = token.split('/');
  if (stepParts.length > 2)
    return 'Only one step separator (/) is allowed per token';

  const [baseToken, stepToken] = stepParts;
  const baseErr = validateAtomicToken(
    baseToken,
    min,
    max,
    allowQuestionMark,
    parseValue,
  );
  if (baseErr) return baseErr;

  if (stepToken !== undefined) {
    const step = parseNumber(stepToken);
    if (step === null) return 'Step value must be an integer';
    if (step <= 0) return 'Step value must be greater than 0';
    if (step > max + 1) {
      return `Step value must be less than or equal to ${max + 1}`;
    }
  }

  return null;
};

const validateCronPartExpr = (
  expr,
  min,
  max,
  allowQuestionMark = false,
  allowAlphabetic = false,
  parseValue = parseNumber,
) => {
  if (!expr) return 'A value is required';
  let chars = '\\d,\\-/*';
  if (allowAlphabetic) chars = 'A-Za-z' + chars;
  if (allowQuestionMark) chars += '?';
  const pattern = new RegExp(`^[${chars}]+$`);
  if (!pattern.test(expr)) {
    return allowQuestionMark ?
        'Only digits, comma, dash, slash, asterisk, and question mark are supported'
      : 'Only digits, comma, dash, slash, and asterisk are supported';
  }

  const tokens = expr.split(',');
  if (tokens.some((token) => token.trim() === '')) {
    return 'List values cannot contain empty items';
  }

  for (const rawToken of tokens) {
    const token = rawToken.trim();
    const tokenErr = validateCronPartToken(
      token,
      min,
      max,
      allowQuestionMark,
      parseValue,
    );
    if (tokenErr) return tokenErr;
  }

  return null;
};

const describeAtomicToken = (
  baseToken,
  unit,
  valueFormatter = passthroughValueFormatter,
) => {
  if (baseToken === '*') return `every ${unit}`;
  if (baseToken === '?') return `unspecified ${unit}`;

  if (baseToken.includes('-')) {
    const [start, end] = baseToken.split('-');
    return `${unit} ${valueFormatter(start)}-${valueFormatter(end)}`;
  }

  return `${unit} ${valueFormatter(baseToken)}`;
};

const describeCronPartExpr = (
  expr,
  unit,
  valueFormatter = passthroughValueFormatter,
) => {
  if (!expr) return `every ${unit}`;

  const tokenDescriptions = expr.split(',').map((rawToken) => {
    const token = rawToken.trim();
    const [baseToken, stepToken] = token.split('/');

    if (stepToken !== undefined) {
      if (baseToken === '*') {
        return `every ${stepToken} ${unit}s`;
      }
      return `every ${stepToken} ${unit}s within ${describeAtomicToken(baseToken, unit, valueFormatter)}`;
    }

    return describeAtomicToken(baseToken, unit, valueFormatter);
  });

  return listFormat(tokenDescriptions);
};

const describeDayDimension = (dayExpr, weekdayExpr) => {
  const dayIsAny = dayExpr === '*';
  const weekdayIsAny = weekdayExpr === '*';
  const dayIsUnspecified = dayExpr === '?';
  const weekdayIsUnspecified = weekdayExpr === '?';

  if (
    (dayIsAny && weekdayIsUnspecified) ||
    (weekdayIsAny && dayIsUnspecified)
  ) {
    return 'every day';
  }

  if (dayIsUnspecified && !weekdayIsUnspecified) {
    return `on ${describeCronPartExpr(weekdayExpr, 'weekday', weekdayValueFormatter)}`;
  }

  if (weekdayIsUnspecified && !dayIsUnspecified) {
    return `on ${describeCronPartExpr(dayExpr, 'day')}`;
  }

  return `on day=${dayExpr}, weekday=${weekdayExpr}`;
};

const DEFAULT_CRON_PARTS = ['00', '00', '*', '*', '*', '?'];

const parseCronSlots = (cronExpr) => {
  if (typeof cronExpr !== 'string' || cronExpr.length === 0) return [];
  const slots = cronExpr.split(' ').slice(0, 6);
  while (slots.length < 6) {
    slots.push('');
  }
  return slots;
};

const parseCronParts = (cronExpr) => {
  if (typeof cronExpr !== 'string' || cronExpr.length === 0) {
    return [...DEFAULT_CRON_PARTS];
  }

  const incoming = parseCronSlots(cronExpr);
  return DEFAULT_CRON_PARTS.map((fallback, idx) => {
    const value = incoming[idx];
    return !value ? fallback : value;
  });
};

const validateCronExpression = (cronExpr) => {
  if (!cronExpr) return 'A CRON expression is required';

  const parts = parseCronSlots(cronExpr);
  if (parts.length !== 6 || parts.some((part) => part.trim() === '')) {
    return 'CRON expression must include 6 fields';
  }

  const secondsToken = parseNumber(parts[0]);
  if (secondsToken !== 0) return 'Seconds must be 0';

  for (let i = 1; i < partConfigs.length; i += 1) {
    const config = partConfigs[i];
    const partErr = validateCronPartExpr(
      parts[i],
      config.min,
      config.max,
      config.allowQuestionMark,
      config.allowAlphabetic,
      config.parseValue,
    );
    if (partErr) return `${config.label}: ${partErr}`;
  }

  const dayPart = parts[3];
  const weekdayPart = parts[5];
  if (dayPart === '?' && weekdayPart === '?') {
    return 'Day and Weekday cannot both be ?';
  }
  if (dayPart !== '?' && weekdayPart !== '?') {
    return 'Use ? for either Day or Weekday to avoid ambiguity';
  }

  return null;
};

// Helper for generating a human-readable summary of a 6-field cron
const cronExprHelperText = (cron) => {
  const parts = parseCronSlots(cron);
  if (parts.length !== 6) return 'Custom schedule expression';

  const [s, m, h, d, mo, wd] = parts;

  if (parseNumber(s) !== 0) return 'Custom schedule expression';

  return `Runs ${describeCronPartExpr(m, 'minute')}, ${describeCronPartExpr(h, 'hour')}, ${describeDayDimension(d, wd)}, ${describeCronPartExpr(mo, 'month')}`;
};

export const CronExprFormField = () => {
  const { formValues, assignValue } = useForm();
  const [advancedByIdx, setAdvancedByIdx] = useState({});
  const rawCronExpr =
    typeof formValues.cronExpr === 'string' ? formValues.cronExpr : '';

  const { onBlur, error } = useFormField(
    'cronExpr',
    ['cronExpr'],
    () => {
      return validateCronExpression(rawCronExpr);
    },
    'CRON job schedule',
  );

  const { entityFormMode } = useEntityMgr();
  const disabled = entityFormMode === EntityFormMode.VIEW;

  const cronParts = useMemo(() => parseCronParts(rawCronExpr), [rawCronExpr]);
  const normalizedCronExpr = cronParts.join(' ');
  const liveValidationError = validateCronExpression(rawCronExpr || normalizedCronExpr);
  const displayError = error || liveValidationError;

  useEffect(() => {
    if (!rawCronExpr) {
      assignValue('cronExpr', normalizedCronExpr);
    }
  }, [assignValue, rawCronExpr, normalizedCronExpr]);

  useEffect(() => {
    if (!formValues.timeZone?.trim()) {
      assignValue('timeZone', DateUtils.timeZoneName());
    }
  }, [assignValue, formValues.timeZone]);

  const isAdvanced = useMemo(
    () =>
      partConfigs.map((config, idx) => {
        if (!config.supportsAdvanced) return false;
        return (
          Boolean(advancedByIdx[idx]) || !config.values.includes(cronParts[idx])
        );
      }),
    [advancedByIdx, cronParts],
  );

  const updatePart = (index, value, options = {}) => {
    const { fromSelector = false } = options;

    const updated = [...cronParts];
    updated[index] = value;

    // In selector mode, keep Day of Month and Day of Week mutually explicit.
    if (fromSelector) {
      const other = index === 3 ? 5 : index === 5 ? 3 : null;
      if (other !== null) {
        if (value !== '?') {
          updated[other] = '?';
        } else if (updated[other] === '?') {
          updated[other] = '*';
        }
      }
    }

    assignValue('cronExpr', updated.join(' '));
  };

  const enableAdvanced = (idx) => {
    setAdvancedByIdx((prev) => ({ ...prev, [idx]: true }));
  };

  const disableAdvanced = (idx) => {
    setAdvancedByIdx((prev) => {
      const updated = { ...prev };
      delete updated[idx];
      return updated;
    });
    if (!partConfigs[idx].values.includes(cronParts[idx])) {
      updatePart(idx, '*');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 0.5,
        }}
      >
        <Box
          component="div"
          sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
        >
          Cron Expression (
          <Box
            component="a"
            href="https://www.freeformatter.com/cron-expression-generator-quartz.html"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Quartz Syntax
          </Box>
          )
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        {partConfigs.map((part, idx) => (
          <Box
            key={part.label}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              minWidth: 110,
            }}
          >
            <Box
              component="span"
              sx={{ fontSize: '0.72rem', color: 'text.secondary', pl: 0.5 }}
            >
              {part.label}
            </Box>
            {isAdvanced[idx] ?
              <TextField
                value={cronParts[idx] ?? ''}
                onChange={(e) => updatePart(idx, e.target.value, { fromSelector: false })}
                onBlur={onBlur}
                size="small"
                variant="outlined"
                placeholder={part.label}
                disabled={disabled}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Chip
                          label="Basic"
                          size="small"
                          onClick={() => disableAdvanced(idx)}
                          disabled={disabled}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            : <Select
                value={cronParts[idx] ?? ''}
                onChange={(e) => {
                  if (e.target.value === '__advanced__') {
                    enableAdvanced(idx);
                    return;
                  }
                  updatePart(idx, e.target.value, { fromSelector: true });
                }}
                onBlur={onBlur}
                displayEmpty
                size="small"
                variant="outlined"
                disabled={disabled || idx === 0}
              >
                {part.values.map((val) => (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                ))}
                {part.supportsAdvanced && (
                  <MenuItem value="__advanced__">Advanced...</MenuItem>
                )}
              </Select>
            }
          </Box>
        ))}
      </Box>
      <FormHelperText error={!!displayError}>
        {displayError || cronExprHelperText(cronParts.join(' '))}
      </FormHelperText>
    </Box>
  );
};
